<template>
	<section class="line-charts">
		<section class="line-chart-row" v-for="rowNum in [1, 2]" :key="rowNum">
			<div class="row-title">
				{{ $t(rowNum === 1 ? 'last_month' : dynamicChartDates) }}
			</div>
			<section class="charts">
				<template v-for="(item, index) in chartsPerLine(rowNum)">
					<div
						:class="[
							'score-chart',
							{ 'small-list': !currentFilteringSite.isTimeSave },
							{ 'single-chart': fullWidthChart }
						]"
						:key="index"
						:style="{ backgroundColor: item.dataset[0].gradientEnd }"
						v-if="!(item.dataset[0].label === $t('time_saved') && !currentFilteringSite.isTimeSave)"
					>
						<div :class="['title', rowNum === 2 ? 'white' : '']">
							<div class="title__top">
								<div>{{ item.dataset[0].label }}</div>
								<button
									v-if="complaintsDevicesStripToggleShown(rowNum, item)"
									type="button"
									class="complaint-strip-toggle"
									:aria-expanded="overviewShowComplaintDevicesInstalled ? 'true' : 'false'"
									:aria-label="$t('overview_complaints_devices_toggle_aria')"
									:title="$t('overview_complaints_devices_toggle_aria')"
									@click.stop="toggleComplaintDevicesInstalled"
								>
									<img
										class="complaint-strip-toggle__finger"
										:class="{ 'is-expanded': overviewShowComplaintDevicesInstalled }"
										:src="fingerClickingIconSrc"
										alt=""
										aria-hidden="true"
									/>
								</button>
							</div>
							<div class="number">
								{{ item.dataset[0].sum }}
								<img
									v-if="rowNum === 1"
									:class="item.dataset[0].isTrendUp ? 'up' : 'down'"
									:style="{ backgroundColor: item.dataset[0].trendArrowBackground }"
									:src="require(`@/assets/performance/trend_white.svg`)"
								/>
								<ArrowIcon
									v-else
									:iconColor="item.dataset[0].trendArrowColor"
									:class="['arrow-bold', item.dataset[0].isTrendUp ? 'up' : 'down']"
								/>
							</div>
						</div>
						<div class="score-chart__chart-wrap">
							<Chart
								type="line"
								:options="getChartOptions(item.dataset[0])"
								:datasets="item.dataset"
								:labels="item.labels"
								height="100%"
								width="100%"
								style="border-radius:10px;"
							/>
						</div>
						<div
							v-if="
								overviewShowComplaintDevicesInstalled &&
								rowNum === 2 &&
								item.complaintDeviceCounts &&
								item.complaintDeviceCounts.length
							"
							class="complaint-device-counts-block"
						>
							<div
								class="complaint-device-counts-caption"
								:class="{ 'on-dark': rowNum === 2 }"
								:title="$t('overview_complaints_devices_caption_hint')"
							>
								{{ $t('overview_complaints_devices_caption') }}
							</div>
							<div class="complaint-device-counts on-dark" dir="ltr">
								<div
									v-for="(n, di) in item.complaintDeviceCounts"
									:key="di"
									class="complaint-device-counts__cell"
									:title="complaintDeviceCellTitle(item, di, n)"
								>
									<span class="complaint-device-counts__mono-line">
										<span class="complaint-device-counts__month">{{
											item.labels[di] != null ? item.labels[di] : '—'
										}}</span>
										<span class="complaint-device-counts__sep">·</span>
										<span class="complaint-device-counts__value">{{
											formatComplaintDeviceCount(n)
										}}</span>
									</span>
								</div>
							</div>
						</div>
					</div>
				</template>
			</section>
		</section>
	</section>
</template>

<script>
import { overviewLineChartsOptions } from '../consts/consts'
import utilService from '../../common/services/utilService'
import Chart from '../../common/components/Chart'
import ArrowIcon from '../../common/icons/ArrowIcon'
import { mapGetters } from 'vuex'

export default {
	props: {
		chartData: { type: Array, required: true },
		singleChart: { type: Boolean, required: false, default: false },
		fullWidthChart: { type: Boolean, required: false, default: false },
		dynamicChartDates: { type: String, required: false }
	},
	computed: {
		...mapGetters(['currentFilteringSite', 'overviewShowComplaintDevicesInstalled']),
		fingerClickingIconSrc() {
			return require('@/assets/finger_clicking.svg')
		},
	},
	methods: {
		complaintsDevicesStripToggleShown(rowNum, item) {
			return (
				rowNum === 2 &&
				item.complaintDeviceCounts &&
				item.complaintDeviceCounts.length > 0
			)
		},
		toggleComplaintDevicesInstalled() {
			this.$store.commit(
				'setOverviewShowComplaintDevicesInstalled',
				!this.overviewShowComplaintDevicesInstalled
			)
		},
		getChartOptions(dataset) {
			const base = JSON.parse(JSON.stringify(overviewLineChartsOptions))
			base.tooltips = { ...overviewLineChartsOptions.tooltips }
			base.tooltips.callbacks = { ...overviewLineChartsOptions.tooltips.callbacks }
			const { max, min } = utilService.findMaxAndMin(dataset.data)
			const range = max - min
			const padding = Math.max(range * 0.3, 2)
			base.scales.yAxes[0].ticks.max = Math.ceil(max + padding)
			base.scales.yAxes[0].ticks.min = Math.max(0, Math.floor(min - padding))
			return base
		},
		formatComplaintDeviceCount(n) {
			if (n === null || n === undefined || Number.isNaN(n)) return '—'
			const num = Number(n)
			if (num >= 1000) {
				return utilService.kFormatter(num)
			}
			if (num >= 100) {
				const k = num / 1000
				const rounded = k >= 1 ? '1k' : `${k.toFixed(1).replace(/\.0$/, '')}k`
				return rounded
			}
			return String(num)
		},
		complaintDeviceCountTitle(n) {
			if (n === null || n === undefined || Number.isNaN(n)) return ''
			return String(Number(n))
		},
		complaintDeviceCellTitle(item, di, n) {
			const month = item.labels && item.labels[di] != null ? String(item.labels[di]) : ''
			const full = this.complaintDeviceCountTitle(n)
			if (month && full) {
				return `${month} — ${full}`
			}
			return full || month || ''
		},
		chartsPerLine(rowNum) {
			const half = Math.ceil(this.chartData.length / 2)
			if (rowNum === 1) {
				if (!this.singleChart) return this.chartData.slice(0, half)
				else if (this.singleChart) return this.chartData.slice(0, 1)
			} else {
				if (!this.singleChart) return this.chartData.slice(half, half * 2)
				else if (this.singleChart) return this.chartData.slice(1, 2)
			}
		}
	},
	beforeDestroy() {
		const el = document.getElementById('overview-line-tooltip')
		if (el) el.remove()
	},
	components: { Chart, ArrowIcon }
}
</script>

<style lang="scss" scoped>
@use "@/styles/mixins" as *;

.line-charts {
	grid-column: 1 / 13;
	grid-row: 1 / 5;
	grid-template-columns: repeat(12, 1fr);
	grid-template-rows: repeat(4, 1fr);
	display: flex;
	margin-inline-end: 20px;
	justify-content: space-between;
	flex-direction: column;
	@include screen-size(xs) {
		margin-inline-end: 0;
	}

	.line-chart-row {
		display: flex;
		flex-direction: column;
		padding-bottom: 10px;
		padding-inline: 16px;
		margin-bottom: 10px;
		height: 50%;
		min-height: 0;
		box-sizing: border-box;
		@include shadow-border;
		@include screen-size(xs) {
			padding-inline: 12px;
		}
		.row-title {
			height: 20%;
			display: flex;
			align-items: center;
			color: #848593;
		}
		.charts {
			width: 100%;
			height: 80%;
			display: flex;
			align-items: stretch;
			justify-content: flex-start;
			gap: 10px;
			min-height: 0;
			@include screen-size(xs) {
				flex-direction: column;
				align-items: stretch;
				gap: 10px;
			}
		}
	}
	.score-chart {
		@include border($grey-border-color);
		border-radius: 10px;
		height: 100%;
		flex: 1 1 0;
		min-width: 0;
		min-height: 0;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		@include screen-size(xs) {
			flex: none;
			width: 100%;
		}
		&__chart-wrap {
			flex: 1 1 0;
			min-height: 0;
			width: 100%;
			position: relative;
			overflow: hidden;
		}
		.title {
			flex: 0 0 auto;
			color: #848593;
			height: 50px;
			padding: 10px 10px 0;
			&.white {
				color: $white;
			}
			.title__top {
				display: flex;
				align-items: flex-start;
				justify-content: space-between;
				gap: 6px;
				margin-bottom: 5px;
				> div:first-child {
					min-width: 0;
				}
			}
			.complaint-strip-toggle {
				flex-shrink: 0;
				padding: 3px 5px;
				margin: -2px -4px 0 0;
				border: none;
				background: transparent;
				cursor: pointer;
				color: inherit;
				line-height: 1;
				border-radius: 4px;
				opacity: 0.88;
				&:hover {
					opacity: 1;
					background: rgba(0, 0, 0, 0.06);
				}
			}
			&.white .complaint-strip-toggle:hover {
				background: rgba(255, 255, 255, 0.12);
			}
			.complaint-strip-toggle__finger {
				display: block;
				width: 20px;
				height: auto;
				object-fit: contain;
				transition: transform 0.2s ease, opacity 0.2s ease;
				transform: rotate(-6deg);
				opacity: 0.92;
				&.is-expanded {
					transform: rotate(2deg) scale(0.96);
					opacity: 1;
				}
			}
			&.white .complaint-strip-toggle__finger {
				filter: brightness(0) invert(1);
			}
			.number {
				display: flex;
				justify-content: space-between;
				font-weight: $font-weight-bold;
				.arrow-bold {
					background-color: $white;
					border-radius: 50%;
					height: 20px;
					width: 20px;
					&.down {
						transform: rotate(180deg);
					}
				}
				img {
					height: 20px;
					border-radius: 50%;
					&.up {
						transform: rotate(180deg);
					}
				}
			}
		}
		.complaint-device-counts-block {
			flex: 0 0 auto;
			min-width: 0;
			max-width: 100%;
		}
	}
	.single-chart {
		flex: none;
		width: 95%;
		max-width: 95%;
	}
}

.complaint-device-counts-block {
	width: 100%;
	min-width: 0;
	max-width: 100%;
	padding: 0 2px 2px;
	box-sizing: border-box;
	flex-shrink: 0;
	margin-top: 0;
	border-top: 1px solid rgba(255, 255, 255, 0.14);
	padding-top: 3px;
	overflow-x: visible;
	overflow-y: visible;
}

.complaint-device-counts-caption {
	font-size: 9px;
	font-weight: $font-weight-semi-bold;
	line-height: 1.1;
	color: $black-blue;
	opacity: 0.88;
	text-align: center;
	margin-bottom: 1px;
	padding: 0 2px;
	cursor: help;
	&.on-dark {
		color: $white;
		opacity: 0.9;
	}
}

.complaint-device-counts {
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	justify-content: flex-start;
	align-items: center;
	gap: 3px 6px;
	width: 100%;
	min-width: 0;
	max-width: 100%;
	padding: 0 0 4px;
	box-sizing: border-box;
	font-size: 9px;
	font-weight: $font-weight-semi-bold;
	font-variant-numeric: tabular-nums;
	color: $black-blue;
	overflow-x: auto;
	overflow-y: hidden;
	-webkit-overflow-scrolling: touch;
	touch-action: pan-x;
	overscroll-behavior-x: contain;
	scrollbar-width: thin;
	scrollbar-color: rgba(255, 255, 255, 0.45) rgba(0, 0, 0, 0.15);
	&::-webkit-scrollbar {
		height: 8px;
	}
	&::-webkit-scrollbar-track {
		background: rgba(0, 0, 0, 0.12);
		border-radius: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.4);
		border-radius: 4px;
	}
	&__cell {
		flex: 0 0 auto;
		max-width: 5.5rem;
		padding: 0 2px;
		line-height: 1.1;
	}
	&__mono-line {
		display: inline-flex;
		flex-direction: row;
		align-items: baseline;
		justify-content: center;
		gap: 2px;
		max-width: 100%;
		white-space: nowrap;
	}
	&__month {
		flex: 0 1 auto;
		min-width: 0;
		font-size: 9px;
		font-weight: $font-weight-semi-bold;
		opacity: 0.85;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	&__sep {
		flex: 0 0 auto;
		opacity: 0.55;
		font-size: 7px;
		padding: 0 1px;
	}
	&__value {
		flex: 0 0 auto;
		font-size: 9px;
		font-weight: $font-weight-bold;
	}
	&.on-dark {
		color: $white;
		scrollbar-color: rgba(255, 255, 255, 0.45) rgba(0, 0, 0, 0.15);
		&::-webkit-scrollbar-thumb {
			background: rgba(255, 255, 255, 0.45);
		}
	}
}
</style>
